/**
 * Per-route search-engine and social metadata.
 *
 * The build ships one index.html for every route, so without this every page
 * sends the same <title> and no description at all. Googlebot renders the app
 * before indexing, so tags written here are read on crawl.
 *
 * Note that social scrapers (WhatsApp, Facebook, LinkedIn, Slack) do NOT run
 * JavaScript. They only ever see the defaults baked into index.html, which is
 * why that file carries a full set of Open Graph tags of its own.
 */

const ORIGIN = 'https://www.flipafrica.app'
const SITE_NAME = 'FLIP Africa'
const SHARE_IMAGE = ORIGIN + '/static/og-image.jpg'

const DEFAULT_TITLE = 'FLIP Africa — Hire vetted professionals in Uganda'
const DEFAULT_DESCRIPTION =
  'FLIP Africa connects Ugandan businesses with vetted freelance professionals — accountants, engineers, project managers and more. Post a job, or find work.'

/**
 * Keyed by route name. Any route not listed falls back to the defaults above,
 * and any route requiring authentication is marked noindex automatically.
 */
const ROUTE_SEO = {
  'main-page': {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION
  },
  'talents': {
    title: 'Hire Vetted Professionals in Uganda | FLIP Africa',
    description:
      'Browse vetted accountants, engineers, surveyors, project coordinators and other professionals across Uganda. See ratings, testimonials and video pitches.'
  },
  'jobs': {
    title: 'Find Jobs and Freelance Work in Uganda | FLIP Africa',
    description:
      'Browse current jobs and freelance opportunities across Uganda. Filter by profession and location, then apply with your FLIP Africa profile.'
  },
  'how-it-works': {
    title: 'How FLIP Africa Works | Post a Job or Find Work',
    description:
      'How businesses post jobs and hire vetted talent on FLIP Africa, and how professionals build a profile with a video pitch, ratings and certificates.'
  },
  'about-us': {
    title: 'About FLIP Africa | Connecting Uganda\'s Talent With Opportunity',
    description:
      'FLIP Africa helps Ugandan businesses hire vetted professionals quickly and affordably, and helps skilled workers show their true potential and find better work.'
  },
  'contact-us': {
    title: 'Contact FLIP Africa',
    description:
      'Get in touch with the FLIP Africa team for help with hiring, your profile or your account.'
  },
  'notice-board': {
    title: 'Notice Board | FLIP Africa'
  },
  'privacy-policy': {
    title: 'Privacy Policy | FLIP Africa',
    description:
      'How FLIP Africa collects, uses and protects your personal information.'
  },
  'terms-of-service': {
    title: 'Terms of Service | FLIP Africa',
    description: 'The terms that govern your use of the FLIP Africa platform.'
  },

  // Real pages, but nothing a search result should ever land on.
  'login': { title: 'Log In | FLIP Africa', noindex: true },
  'sign-up': { title: 'Sign Up | FLIP Africa', noindex: true },
  'reset-password': { title: 'Reset Your Password | FLIP Africa', noindex: true },
  'post-job': { title: 'Post a Job | FLIP Africa', noindex: true }
}

function upsertMeta (attr, name, content) {
  let el = document.head.querySelector('meta[' + attr + '="' + name + '"]')
  if (!content) {
    if (el) el.parentNode.removeChild(el)
    return
  }
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertCanonical (href) {
  let el = document.head.querySelector('link[rel="canonical"]')
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

/**
 * Apply metadata for a resolved route. Safe to call on every navigation.
 */
export function applyRouteSeo (to) {
  const seo = ROUTE_SEO[to.name] || {}
  const requiresAuth = to.matched.some(record => record.meta && record.meta.requiresAuth)

  const title = seo.title || DEFAULT_TITLE
  const description = seo.description || DEFAULT_DESCRIPTION
  // Path only, so query strings and the apex host never produce duplicate URLs.
  const canonical = ORIGIN + to.path

  document.title = title
  upsertCanonical(canonical)
  upsertMeta('name', 'description', description)
  upsertMeta('name', 'robots', seo.noindex || requiresAuth ? 'noindex, follow' : null)

  upsertMeta('property', 'og:site_name', SITE_NAME)
  upsertMeta('property', 'og:type', 'website')
  upsertMeta('property', 'og:url', canonical)
  upsertMeta('property', 'og:title', title)
  upsertMeta('property', 'og:description', description)
  upsertMeta('property', 'og:image', SHARE_IMAGE)

  upsertMeta('name', 'twitter:card', 'summary_large_image')
  upsertMeta('name', 'twitter:title', title)
  upsertMeta('name', 'twitter:description', description)
  upsertMeta('name', 'twitter:image', SHARE_IMAGE)
}

export default { applyRouteSeo }
